<script setup>
import { useI18n } from 'vue-i18n';
import { assoc, isEmpty, pipe } from 'ramda';
import { useForm } from 'vee-validate';
import * as yup from 'yup';
import { toValue } from 'vue';

import WithLoader from '@/components/WithLoader.vue';
import InPlaceAlert from '@/components/InPlaceAlert.vue';
import AccountFormField from '@/components/forms/AccountFormField.vue';
import AccountFormFieldSelect from '@/components/forms/AccountFormFieldSelect.vue';

import useCallEndPointWith from '@/services/backend/useCallEndPointWith';
import { companyDetailsSpec } from '@/services/register';
import { countryOptions } from '@/services/formUtilities';
import { useGlobalState } from '@/store/user.store';

const { t } = useI18n();
const emit = defineEmits(['detailsConfirmed', 'back']);

const { userState } = useGlobalState();

const { defineField, handleSubmit } = useForm({
  validationSchema: yup.object({
    company: yup.object({
      companyName: yup
        .string()
        .min(3)
        .required()
        .label(t('registerView.accountForm.labels.companyName')),
      country: yup
        .string()
        .required()
        .label(t('registerView.accountForm.labels.country')),
    }).required(),
  }),
});

const [companyName, companyNameAttrs] = defineField('company.companyName');
const [country, countryAttrs] = defineField('company.country');

const {
  callEndpoint,
  userFriendlyError,
  isLoading,
} = useCallEndPointWith('createCompany');

const submit = handleSubmit(async (values) => {
  const request = pipe(companyDetailsSpec)(values);

  await callEndpoint(assoc('method', 'POST', { request }));

  if (isEmpty(userFriendlyError.value)) {
    emit('detailsConfirmed', {
      user: toValue(userState),
      email: userState.value.email,
    });
  }
});
</script>

<template>
  <div>
    <form
      @submit="submit"
      class="m-10"
    >
      <div class="max-w-4xl mx-auto px-16 py-8 base-gradient text-primary rounded-lg gap-x-3">
        <h2 class="text-2xl">
          {{ $t('registerView.accountForm.companyDetailsTitle') }}
        </h2>
        <div class="px-4 pb-36 pt-8 sm:px-6 lg:px-0 lg:pb-16">
          <div class="grid grid-cols-1 md:grid-cols-2 gap-x-5 gap-y-3 items-center">
            <InPlaceAlert
              :message="userFriendlyError"
              type="error"
              class="col-span-2"
              @close-alert="userFriendlyError = ''"
            />

            <AccountFormField
              id="company.companyName"
              :label="$t('registerView.accountForm.labels.companyName')"
              :is-required="true"
              v-model="companyName"
              input-type="text"
              v-bind="companyNameAttrs"
              auto-complete-type="organization"
            />

            <AccountFormFieldSelect
              id="company.country"
              :label="$t('registerView.accountForm.labels.country')"
              :is-required="true"
              v-model="country"
              v-bind="countryAttrs"
              :options="countryOptions"
            />
          </div>
        </div>
      </div>

      <div class="flex flex-row justify-end max-w-4xl mx-auto mt-8">
        <WithLoader :is-loading="isLoading">
          <button
            type="submit"
            class="w-full mt-4 px-14 py-2 btn-cta sm:order-last sm:w-auto"
          >
            {{ $t('registerView.accountForm.confirm') }}
          </button>
        </WithLoader>
      </div>
    </form>
  </div>
</template>
